import type { StageStateV1 } from '@shotstead/contracts'

const ROOM_WIDTH_METERS = 6
const ROOM_DEPTH_METERS = 5
const VIEW_WIDTH = 600
const VIEW_HEIGHT = 500

function projectX(x: number) {
  return ((x + ROOM_WIDTH_METERS / 2) / ROOM_WIDTH_METERS) * VIEW_WIDTH
}

function projectZ(z: number) {
  return VIEW_HEIGHT - ((z + ROOM_DEPTH_METERS / 2) / ROOM_DEPTH_METERS) * VIEW_HEIGHT
}

function unprojectX(x: number) {
  return (x / VIEW_WIDTH) * ROOM_WIDTH_METERS - ROOM_WIDTH_METERS / 2
}

function unprojectZ(y: number) {
  return ((VIEW_HEIGHT - y) / VIEW_HEIGHT) * ROOM_DEPTH_METERS - ROOM_DEPTH_METERS / 2
}

function stageRefPosition(stage: StageStateV1, ref: string | null) {
  if (!ref) return null
  const performer = stage.performers.find((item) => item.performer_id === ref)
  if (performer) return performer.transform.position
  const prop = stage.props.find((item) => item.prop_id === ref)
  return prop?.transform.position ?? null
}

export function TopDownStage({
  stage,
  onCameraPosition,
}: {
  stage: StageStateV1
  onCameraPosition: (x: number, z: number) => void
}) {
  const camera = stage.camera.transform.position
  const target = stageRefPosition(stage, stage.camera.target_ref)
  const targetX = target ? projectX(target.x) : projectX(camera.x)
  const targetY = target ? projectZ(target.z) : projectZ(camera.z - 1)
  const cameraX = projectX(camera.x)
  const cameraY = projectZ(camera.z)
  const angle = Math.atan2(targetY - cameraY, targetX - cameraX)
  const coneLength = 70
  const coneWidth = 35
  const conePoints = [
    [cameraX, cameraY],
    [
      cameraX + Math.cos(angle - 0.35) * coneLength,
      cameraY + Math.sin(angle - 0.35) * coneLength,
    ],
    [
      cameraX + Math.cos(angle + 0.35) * coneLength,
      cameraY + Math.sin(angle + 0.35) * coneLength,
    ],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(' ')

  const onStageClick = (event: React.MouseEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const localX = ((event.clientX - bounds.left) / bounds.width) * VIEW_WIDTH
    const localY = ((event.clientY - bounds.top) / bounds.height) * VIEW_HEIGHT
    onCameraPosition(unprojectX(localX), unprojectZ(localY))
  }

  return (
    <svg
      className="topdown-stage"
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      role="img"
      aria-label="2D top-down stage editor"
      onClick={onStageClick}
    >
      <rect x="2" y="2" width={VIEW_WIDTH - 4} height={VIEW_HEIGHT - 4} rx="8" className="topdown-room" />

      <rect x="72" y="30" width="104" height="16" rx="5" className="topdown-window" />
      <rect x="6" y="305" width="16" height="100" rx="5" className="topdown-door" />
      <rect
        x={projectX(0.65) - 75}
        y={projectZ(0.2) - 40}
        width="150"
        height="80"
        rx="8"
        className="topdown-table"
      />

      {stage.motion_cues.map((cue) => {
        if (cue.path.length < 2) return null
        const points = cue.path.map((point) => `${projectX(point.position.x)},${projectZ(point.position.z)}`).join(' ')
        return <polyline key={cue.cue_id} points={points} className="topdown-path" data-testid={`path-${cue.cue_id}`} />
      })}

      {stage.props.map((prop) => (
        <g
          key={prop.prop_id}
          transform={`translate(${projectX(prop.transform.position.x)} ${projectZ(prop.transform.position.z)})`}
          data-testid={`topdown-prop-${prop.prop_id}`}
        >
          <rect x="-9" y="-7" width="18" height="14" rx="3" className="topdown-prop" />
          <text x="13" y="4" className="topdown-label">prop</text>
        </g>
      ))}

      {stage.performers.map((performer) => (
        <g
          key={performer.performer_id}
          transform={`translate(${projectX(performer.transform.position.x)} ${projectZ(performer.transform.position.z)})`}
          data-testid={`topdown-performer-${performer.performer_id}`}
        >
          <circle r="12" className="topdown-performer" />
          <line x1="0" y1="0" x2="18" y2="0" className="topdown-facing" />
          <text x="16" y="-14" className="topdown-label">performer</text>
        </g>
      ))}

      <polygon points={conePoints} className="topdown-camera-cone" />
      <g
        transform={`translate(${cameraX} ${cameraY})`}
        data-testid="topdown-camera"
        data-camera-x={camera.x.toFixed(3)}
        data-camera-z={camera.z.toFixed(3)}
      >
        <rect x="-12" y="-8" width="24" height="16" rx="4" className="topdown-camera" />
        <text x="17" y="4" className="topdown-label">camera</text>
      </g>

      <text x="18" y="26" className="topdown-help">Click the floor to move Camera X/Z</text>
    </svg>
  )
}
